import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography, TypographyComponents } from '../typography/Typography';
import { Html } from '../html/Html';
import { Cancel, Minus, Star } from '../icon';
import { TickCircle } from '../icon/icons/TickCircle';
import { Stack } from '../stack/Stack';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import tokens from './tokens';
import { ThemeSpaceKeys } from '../../styles/tokens/space';

// Interfaces remain the same
export interface IconListProps {
  centerAlign?: boolean;
  contentSmall?: boolean;
  displayAsGrid?: boolean;
  icon?: string | React.ReactNode;
  iconColor?: ThemeColorsKeys;
  items?: IconListItemsProps[];
  large?: boolean;
  product?: boolean;
  titleTypographyComponent?: TypographyComponents;
  listGap?: ThemeSpaceKeys;
  listGapMobile?: ThemeSpaceKeys;
  listItemGap?: ThemeSpaceKeys; // Add this new prop
  verticalItems?: boolean;
  textAlignCenter?: boolean;
}

export interface IconListItemsProps {
  content?: string;
  icon?: JSX.Element; // Option react component per item
  iconItem?: string;
  id?: string;
  title?: string;
  listItemGap?: ThemeSpaceKeys;
}

interface StyledIconListProps {
  centerAlign?: boolean;
  displayAsGrid?: boolean;
  large?: boolean;
  listGap?: ThemeSpaceKeys;
  listItemGap?: ThemeSpaceKeys; // Add this new prop
  listGapMobile?: ThemeSpaceKeys;
  numberOfColumns?: number;
  verticalItems?: boolean;
  textAlignCenter?: boolean;
}

// Styles remain the same
export const StyledIconList = styled.ul<StyledIconListProps>`
  ${({
    theme: { media, space },
    displayAsGrid,
    numberOfColumns,
    listGap,
    listGapMobile,
  }) => css`
    display: grid;
    gap: ${listGap ? space[listGapMobile] ?? space[listGap] : space.small};

    @media (min-width: ${media.medium}) {
      ${displayAsGrid &&
      css`
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: ${space.small};
      `}
    }
    @media (min-width: ${media.large}) {
      gap: ${listGap ? space[listGap] : space.small};
      ${displayAsGrid &&
      css`
        grid-template-columns: repeat(${numberOfColumns}, 1fr);
      `}
    }
  `};
`;

const StyledIconListItem = styled.li<StyledIconListProps>`
  ${({
    theme: { space },
    centerAlign,
    listGap,
    listItemGap,
    verticalItems,
    textAlignCenter,
  }) => css`
    display: flex;
    align-items: ${centerAlign ? 'center' : 'flex-start'};
    gap: ${listItemGap
      ? space[listItemGap]
      : space.small}; // Use listItemGap here

    ${verticalItems &&
    css`
      flex-direction: column;
      align-items: center;
    `}

    ${textAlignCenter &&
    css`
      text-align: center;
    `}
  `};
`;

const StyledStack = styled(Stack)<StyledIconListProps>`
  ${({ theme: { space }, centerAlign }) => css`
    padding-top: ${centerAlign ? '0' : space.xxxSmall};
  `};
`;

export const StyledNoBullet = styled.div`
  ${() => css`
    & ul > li {
      padding-left: 0 !important;
    }
    & ul > li:before {
      display: none;
    }
  `};
`;

// Helper functions remain the same
const getIcon = (iconType?: string, iconColor?: ThemeColorsKeys) => {
  switch (iconType) {
    case 'tick':
      return <TickCircle color={iconColor ?? tokens.tickColour} />;
    case 'cross':
      return <Cancel color={iconColor ?? 'brandOne100'} />;
    case 'minus':
      return <Minus color={iconColor ?? 'brandOne100'} />;
    case 'star':
      return <Star color={iconColor ?? 'brandOne100'} />;
    default:
      return <TickCircle color={iconColor ?? tokens.tickColour} />;
  }
};

const determineVariant = (large, product) => {
  if (large) return 'headingSmall';
  if (product) return 'bodyMedium';
  return 'bodyMediumBold';
};

// Modified component with conditional rendering
export const IconList: FC<IconListProps> = ({
  centerAlign,
  contentSmall,
  displayAsGrid,
  icon,
  iconColor = tokens.tickColour,
  items,
  large,
  listGap = 'small',
  listGapMobile,
  listItemGap,
  product,
  titleTypographyComponent,
  verticalItems,
  textAlignCenter,
  ...rest
}) => {
  if (!items) {
    return null;
  }

  return (
    <StyledIconList
      as="ul"
      asGrid={displayAsGrid}
      centerAlign={centerAlign}
      displayAsGrid={displayAsGrid}
      large={large}
      numberOfColumns={items.length <= 3 ? items.length : 3}
      product={product}
      listGap={listGap}
      listGapMobile={listGapMobile}
      {...rest}
    >
      {items.map((item, index) => (
        <StyledIconListItem
          centerAlign={centerAlign}
          displayAsGrid={displayAsGrid}
          listGap={listGap}
          listItemGap={item.listItemGap || listItemGap} // Use item's listItemGap if available, otherwise use the prop
          verticalItems={verticalItems}
          textAlignCenter={textAlignCenter}
          key={index}
        >
          {item.icon ? (
            item.icon
          ) : (
            <>
              {item.iconItem
                ? getIcon(item.iconItem, iconColor)
                : getIcon(icon, iconColor)}
            </>
          )}

          <StyledStack gap="xxSmall" centerAlign={centerAlign}>
            {item.title && (
              <Typography
                component={titleTypographyComponent || 'h4'}
                variant={determineVariant(large, product)}
              >
                {item.title}
              </Typography>
            )}
            {item.content && <Html small={contentSmall}>{item.content}</Html>}
          </StyledStack>
        </StyledIconListItem>
      ))}
    </StyledIconList>
  );
};

export default IconList;
